import React, { useState, useEffect, useMemo } from 'react';
import { api, useAuth } from '@/contexts/AuthContext';
import { formatDate, formatStatus, getStatusColor } from '@/lib/utils';
import { Plus, AlertTriangle, Search, Edit2 } from 'lucide-react';
import { Pagination } from '@/components/Pagination';

const SEVERITIES = ['MINOR', 'MODERATE', 'SERIOUS', 'FATAL'];
const INCIDENT_TYPES = ['NEAR_MISS', 'FIRST_AID', 'MEDICAL_TREATMENT', 'LOST_TIME', 'FATALITY', 'PROPERTY_DAMAGE', 'ENVIRONMENTAL'];
const STATUSES = ['REPORTED', 'UNDER_INVESTIGATION', 'CORRECTIVE_ACTION_TAKEN'];
const BODY_PARTS = ['Head', 'Face/Eyes', 'Neck', 'Chest', 'Back', 'Shoulder', 'Arm', 'Hand/Fingers', 'Abdomen', 'Hip', 'Leg', 'Knee', 'Foot/Toes', 'Multiple', 'Other'];
const INJURED_PERSON_TYPES = ['EMPLOYEE', 'CONTRACTOR', 'VISITOR'];

const PAGE_SIZE = 50;

export default function IncidentsPage() {
  const { user } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editInc, setEditInc] = useState(null);
  const [severityFilter, setSeverityFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchIncidents = (p = page) => {
    const skip = (p - 1) * PAGE_SIZE;
    let url = `/incidents?skip=${skip}&limit=${PAGE_SIZE}`;
    if (severityFilter) url += `&severity=${severityFilter}`;
    api.get(url).then(r => {
      setIncidents(r.data.incidents || []);
      setTotalCount(r.data.total_count || 0);
    }).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchIncidents(page); }, [page, severityFilter]);
  useEffect(() => { api.get('/projects').then(r => setProjects(r.data.projects || [])); }, []);

  const canCreate = ['DIRECTOR', 'SUPERVISOR', 'HR_ADMIN'].includes(user?.role);

  return (
    <div data-testid="incidents-page" className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 font-heading tracking-tight">Safety Incidents</h1>
          <p className="text-sm text-slate-500">{totalCount} incidents</p>
        </div>
        {canCreate && (
          <button onClick={() => setShowCreate(true)} data-testid="create-incident-btn"
            className="bg-red-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-700 flex items-center gap-2">
            <Plus size={16} /> Report Incident
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <select value={severityFilter} onChange={e => { setSeverityFilter(e.target.value); setPage(1); }} className="border border-slate-200 rounded-md px-3 py-2 text-sm outline-none" data-testid="filter-severity">
          <option value="">All Severities</option>
          {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[700px]">
          <thead><tr className="border-b border-slate-100 bg-slate-50/50">
            <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Code</th>
            <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Date</th>
            <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Type</th>
            <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Severity</th>
            <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Location</th>
            <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Reported By</th>
            <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</th>
            <th className="w-12"></th>
          </tr></thead>
          <tbody>
            {incidents.map(inc => (
              <tr key={inc.id} className="border-b border-slate-50 hover:bg-slate-50" data-testid={`inc-row-${inc.id}`}>
                <td className="py-2.5 px-4 font-mono text-xs text-red-700 font-medium">{inc.incident_code}</td>
                <td className="py-2.5 px-4 text-slate-500">{formatDate(inc.incident_date)}</td>
                <td className="py-2.5 px-4"><span className="text-xs px-1.5 py-0.5 rounded bg-slate-100">{formatStatus(inc.incident_type)}</span></td>
                <td className="py-2.5 px-4"><SeverityBadge severity={inc.severity} /></td>
                <td className="py-2.5 px-4">{inc.location || '-'}</td>
                <td className="py-2.5 px-4">{inc.reported_by || '-'}</td>
                <td className="py-2.5 px-4"><span className={`text-xs px-2 py-0.5 rounded-full ${
                  inc.status === 'REPORTED' ? 'bg-red-100 text-red-700' :
                  inc.status === 'UNDER_INVESTIGATION' ? 'bg-amber-100 text-amber-700' :
                  'bg-green-100 text-green-700'
                }`}>{formatStatus(inc.status)}</span></td>
                <td className="py-2.5 px-2">
                  {canCreate && <button onClick={() => setEditInc({...inc})} className="text-slate-400 hover:text-red-600" data-testid={`edit-inc-${inc.id}`}><Edit2 size={14} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {incidents.length === 0 && <div className="text-center py-12 text-slate-400">No incidents found</div>}
        <Pagination currentPage={page} totalCount={totalCount} pageSize={PAGE_SIZE} onPageChange={setPage} />
      </div>

      {showCreate && <CreateIncidentModal projects={projects} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchIncidents(); }} />}
      {editInc && <EditIncidentModal incident={editInc} onClose={() => setEditInc(null)} onSaved={() => { setEditInc(null); fetchIncidents(); }} />}
    </div>
  );
}

function SeverityBadge({ severity }) {
  const colors = {
    MINOR: 'bg-green-100 text-green-700',
    MODERATE: 'bg-amber-100 text-amber-700',
    SERIOUS: 'bg-orange-100 text-orange-700',
    FATAL: 'bg-red-100 text-red-700',
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${colors[severity] || 'bg-slate-100 text-slate-600'}`}>{severity}</span>;
}

function CreateIncidentModal({ projects, onClose, onCreated }) {
  const [form, setForm] = useState({
    project_id: '', incident_date: new Date().toISOString().slice(0, 10), incident_time: '',
    location: '', severity: 'MINOR', incident_type: 'NEAR_MISS', description: '',
    injured_person_name: '', injured_person_type: '', injury_description: '',
    body_part_affected: '', first_aid_given: false, first_aid_details: '',
    hospital_visit_required: false, emergency_services_called: false, site_secured: false,
    equipment_details: '', serial_asset_no: '', supervisor_notified: false,
    notification_datetime: '', witness_names: '', root_cause: '',
    corrective_actions: '', preventive_actions: '', ppe_worn: '',
    supervisor_name: '', supervisor_remarks: ''
  });
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState('basic');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try { await api.post('/incidents', form); onCreated(); } catch (err) { alert(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  const sections = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'injury', label: 'Injury Details' },
    { id: 'response', label: 'Response' },
    { id: 'equipment', label: 'Equipment' },
    { id: 'investigation', label: 'Investigation' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg border w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="create-incident-modal">
        <h2 className="text-lg font-semibold text-slate-900 font-heading mb-3 flex items-center gap-2">
          <AlertTriangle size={18} className="text-red-500" /> Report Safety Incident
        </h2>

        <div className="flex gap-1 mb-4 flex-wrap">
          {sections.map(s => (
            <button key={s.id} onClick={() => setSection(s.id)}
              className={`px-3 py-1 text-xs font-medium rounded-full ${section === s.id ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
              {s.label}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {section === 'basic' && <>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-700 mb-1 block">Project</label>
                <select value={form.project_id} onChange={e => setForm({...form, project_id: e.target.value})} className="input-field w-full" data-testid="inc-project">
                  <option value="">Select</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.project_code} - {p.name}</option>)}
                </select></div>
              <div><label className="text-xs font-medium text-slate-700 mb-1 block">Severity *</label>
                <select value={form.severity} onChange={e => setForm({...form, severity: e.target.value})} className="input-field w-full" data-testid="inc-severity">
                  {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
                </select></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-700 mb-1 block">Incident Type</label>
                <select value={form.incident_type} onChange={e => setForm({...form, incident_type: e.target.value})} className="input-field w-full" data-testid="inc-type">
                  {INCIDENT_TYPES.map(t => <option key={t} value={t}>{formatStatus(t)}</option>)}
                </select></div>
              <div><label className="text-xs font-medium text-slate-700 mb-1 block">Location</label>
                <input value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="input-field w-full" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-700 mb-1 block">Date *</label>
                <input type="date" value={form.incident_date} onChange={e => setForm({...form, incident_date: e.target.value})} className="input-field w-full" required /></div>
              <div><label className="text-xs font-medium text-slate-700 mb-1 block">Time</label>
                <input type="time" value={form.incident_time} onChange={e => setForm({...form, incident_time: e.target.value})} className="input-field w-full" /></div>
            </div>
            <div><label className="text-xs font-medium text-slate-700 mb-1 block">Description *</label>
              <textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} rows={3} className="input-field w-full" required data-testid="inc-description" /></div>
          </>}

          {section === 'injury' && <>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-700 mb-1 block">Injured Person Name</label>
                <input value={form.injured_person_name} onChange={e => setForm({...form, injured_person_name: e.target.value})} className="input-field w-full" data-testid="inc-injured-name" /></div>
              <div><label className="text-xs font-medium text-slate-700 mb-1 block">Person Type</label>
                <select value={form.injured_person_type} onChange={e => setForm({...form, injured_person_type: e.target.value})} className="input-field w-full" data-testid="inc-injured-type">
                  <option value="">Select</option>
                  {INJURED_PERSON_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select></div>
            </div>
            <div><label className="text-xs font-medium text-slate-700 mb-1 block">Injury Description</label>
              <textarea value={form.injury_description} onChange={e => setForm({...form, injury_description: e.target.value})} rows={2} className="input-field w-full" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-700 mb-1 block">Body Part Affected</label>
                <select value={form.body_part_affected} onChange={e => setForm({...form, body_part_affected: e.target.value})} className="input-field w-full" data-testid="inc-body-part">
                  <option value="">Select</option>
                  {BODY_PARTS.map(b => <option key={b} value={b}>{b}</option>)}
                </select></div>
              <div><label className="text-xs font-medium text-slate-700 mb-1 block">PPE Worn</label>
                <input value={form.ppe_worn} onChange={e => setForm({...form, ppe_worn: e.target.value})} className="input-field w-full" placeholder="e.g. Helmet, Gloves" /></div>
            </div>
          </>}

          {section === 'response' && <>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 rounded-md p-2.5">
                <input type="checkbox" checked={form.first_aid_given} onChange={e => setForm({...form, first_aid_given: e.target.checked})} className="w-4 h-4" /> First Aid Given
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 rounded-md p-2.5">
                <input type="checkbox" checked={form.hospital_visit_required} onChange={e => setForm({...form, hospital_visit_required: e.target.checked})} className="w-4 h-4" /> Hospital Visit Required
              </label>
            </div>
            {form.first_aid_given && <div><label className="text-xs font-medium text-slate-700 mb-1 block">First Aid Details</label>
              <textarea value={form.first_aid_details} onChange={e => setForm({...form, first_aid_details: e.target.value})} rows={2} className="input-field w-full" /></div>}
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 rounded-md p-2.5">
                <input type="checkbox" checked={form.emergency_services_called} onChange={e => setForm({...form, emergency_services_called: e.target.checked})} className="w-4 h-4" data-testid="inc-emergency" /> Emergency Services Called
              </label>
              <label className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 rounded-md p-2.5">
                <input type="checkbox" checked={form.site_secured} onChange={e => setForm({...form, site_secured: e.target.checked})} className="w-4 h-4" data-testid="inc-site-secured" /> Site Secured
              </label>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex items-center gap-2 text-sm text-slate-700 bg-slate-50 rounded-md p-2.5">
                <input type="checkbox" checked={form.supervisor_notified} onChange={e => setForm({...form, supervisor_notified: e.target.checked})} className="w-4 h-4" data-testid="inc-supervisor-notified" /> Supervisor Notified
              </label>
              <div><label className="text-xs font-medium text-slate-700 mb-1 block">Notification Date/Time</label>
                <input type="datetime-local" value={form.notification_datetime} onChange={e => setForm({...form, notification_datetime: e.target.value})} className="input-field w-full" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-slate-700 mb-1 block">Supervisor Name</label>
                <input value={form.supervisor_name} onChange={e => setForm({...form, supervisor_name: e.target.value})} className="input-field w-full" /></div>
              <div><label className="text-xs font-medium text-slate-700 mb-1 block">Witness Names</label>
                <input value={form.witness_names} onChange={e => setForm({...form, witness_names: e.target.value})} className="input-field w-full" /></div>
            </div>
          </>}

          {section === 'equipment' && <>
            <div><label className="text-xs font-medium text-slate-700 mb-1 block">Equipment/Machine Details</label>
              <textarea value={form.equipment_details} onChange={e => setForm({...form, equipment_details: e.target.value})} rows={2} className="input-field w-full" data-testid="inc-equipment" /></div>
            <div><label className="text-xs font-medium text-slate-700 mb-1 block">Serial / Asset Number</label>
              <input value={form.serial_asset_no} onChange={e => setForm({...form, serial_asset_no: e.target.value})} className="input-field w-full" data-testid="inc-serial-no" /></div>
          </>}

          {section === 'investigation' && <>
            <div><label className="text-xs font-medium text-slate-700 mb-1 block">Root Cause</label>
              <textarea value={form.root_cause} onChange={e => setForm({...form, root_cause: e.target.value})} rows={2} className="input-field w-full" /></div>
            <div><label className="text-xs font-medium text-slate-700 mb-1 block">Corrective Actions</label>
              <textarea value={form.corrective_actions} onChange={e => setForm({...form, corrective_actions: e.target.value})} rows={2} className="input-field w-full" /></div>
            <div><label className="text-xs font-medium text-slate-700 mb-1 block">Preventive Actions</label>
              <textarea value={form.preventive_actions} onChange={e => setForm({...form, preventive_actions: e.target.value})} rows={2} className="input-field w-full" /></div>
            <div><label className="text-xs font-medium text-slate-700 mb-1 block">Supervisor Remarks</label>
              <textarea value={form.supervisor_remarks} onChange={e => setForm({...form, supervisor_remarks: e.target.value})} rows={2} className="input-field w-full" /></div>
          </>}

          <div className="flex gap-3 pt-2 border-t border-slate-100">
            <button type="button" onClick={onClose} className="flex-1 border border-slate-200 py-2 rounded-md text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="submit-incident-btn" className="flex-1 bg-red-600 text-white py-2 rounded-md text-sm font-medium hover:bg-red-700 disabled:opacity-60">
              {saving ? 'Reporting...' : 'Report Incident'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditIncidentModal({ incident, onClose, onSaved }) {
  const [form, setForm] = useState({ ...incident });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/incidents/${incident.id}`, {
        status: form.status,
        severity: form.severity,
        root_cause: form.root_cause,
        corrective_actions: form.corrective_actions,
        preventive_actions: form.preventive_actions,
        supervisor_remarks: form.supervisor_remarks,
      });
      onSaved();
    } catch (err) { alert('Save failed'); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-lg border w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="edit-incident-modal">
        <h2 className="text-lg font-semibold text-slate-900 font-heading mb-4">Update Incident - {incident.incident_code}</h2>
        <div className="space-y-3">
          <div className="bg-slate-50 rounded-lg p-3 text-xs space-y-1">
            <div className="flex justify-between"><span className="text-slate-500">Severity</span><SeverityBadge severity={incident.severity} /></div>
            <div className="flex justify-between"><span className="text-slate-500">Description</span><span className="text-right max-w-[60%]">{incident.description?.slice(0, 80)}...</span></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-medium text-slate-700 mb-1 block">Status</label>
              <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="input-field w-full" data-testid="edit-inc-status">
                {STATUSES.map(s => <option key={s} value={s}>{formatStatus(s)}</option>)}
              </select></div>
            <div><label className="text-xs font-medium text-slate-700 mb-1 block">Severity</label>
              <select value={form.severity} onChange={e => setForm({...form, severity: e.target.value})} className="input-field w-full">
                {SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
              </select></div>
          </div>
          <div><label className="text-xs font-medium text-slate-700 mb-1 block">Root Cause</label>
            <textarea value={form.root_cause || ''} onChange={e => setForm({...form, root_cause: e.target.value})} rows={2} className="input-field w-full" /></div>
          <div><label className="text-xs font-medium text-slate-700 mb-1 block">Corrective Actions</label>
            <textarea value={form.corrective_actions || ''} onChange={e => setForm({...form, corrective_actions: e.target.value})} rows={2} className="input-field w-full" /></div>
          <div><label className="text-xs font-medium text-slate-700 mb-1 block">Supervisor Remarks</label>
            <textarea value={form.supervisor_remarks || ''} onChange={e => setForm({...form, supervisor_remarks: e.target.value})} rows={2} className="input-field w-full" /></div>
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 border border-slate-200 py-2 rounded-md text-sm">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="save-incident-btn" className="flex-1 bg-red-600 text-white py-2 rounded-md text-sm font-medium disabled:opacity-60">
              {saving ? 'Saving...' : 'Save'}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
